import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { site } from '../../site';
import { FacebookIcon } from './FacebookIcon';

type Store = 'apple' | 'google';

/**
 * Which listing to put first. A guess from the user agent only orders the
 * buttons — both are always shown, because a guess can be wrong (iPadOS
 * reports itself as a Mac) and someone may be choosing for another phone.
 */
function preferredStore(): Store {
  const ua = typeof navigator === 'undefined' ? '' : navigator.userAgent;
  const apple =
    /iPhone|iPad|iPod/.test(ua) ||
    (/Macintosh/.test(ua) &&
      typeof navigator !== 'undefined' &&
      navigator.maxTouchPoints > 1);
  return apple ? 'apple' : 'google';
}

const STORES: Record<
  Store,
  { href: string; overline: string; name: string; icon: JSX.Element }
> = {
  apple: {
    href: site.appStoreUrl,
    overline: 'Download on the',
    name: 'App Store',
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true" className="h-7 w-7 fill-current">
        <path d="M16.37 12.6c-.02-2.3 1.88-3.4 1.96-3.46-1.07-1.56-2.73-1.78-3.32-1.8-1.41-.14-2.76.83-3.47.83-.72 0-1.82-.81-2.99-.79-1.54.02-2.96.9-3.75 2.27-1.6 2.78-.41 6.89 1.15 9.14.76 1.1 1.67 2.34 2.86 2.3 1.15-.05 1.58-.74 2.97-.74 1.38 0 1.77.74 2.98.72 1.24-.02 2.02-1.12 2.77-2.23.88-1.28 1.24-2.52 1.26-2.59-.03-.01-2.4-.92-2.42-3.65zM14.1 5.86c.63-.77 1.06-1.83.94-2.9-.91.04-2.02.61-2.67 1.37-.58.67-1.1 1.76-.96 2.8 1.02.08 2.06-.52 2.69-1.27z" />
      </svg>
    ),
  },
  google: {
    href: site.playStoreUrl,
    overline: 'Get it on',
    name: 'Google Play',
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true" className="h-7 w-7 fill-current">
        <path d="M3.6 2.2c-.23.25-.36.63-.36 1.12v17.36c0 .49.13.87.37 1.12l.06.05L13.4 12.1v-.2L3.66 2.15l-.06.05zm13.05 13.15-3.25-3.25v-.2l3.25-3.25.07.04 3.85 2.19c1.1.62 1.1 1.65 0 2.27l-3.85 2.19-.07.01zm-.07.04L13.3 12 3.6 21.8c.36.38.95.43 1.62.05l11.36-6.46m0-6.78L5.22 2.15c-.67-.38-1.26-.33-1.62.05L13.3 12l3.28-3.39z" />
      </svg>
    ),
  },
};

function StoreButton({
  store,
  primary,
  linkRef,
}: {
  store: Store;
  primary: boolean;
  linkRef?: React.Ref<HTMLAnchorElement>;
}) {
  const { href, overline, name, icon } = STORES[store];
  return (
    <a
      ref={linkRef}
      href={href}
      target="_blank"
      rel="noreferrer"
      className={
        'flex w-full items-center gap-3 rounded-card px-5 py-3 transition ' +
        (primary
          ? 'bg-ink text-white hover:bg-ink/90'
          : 'border-2 border-ink/15 text-ink hover:border-ink/40')
      }
    >
      {icon}
      <span className="text-left leading-tight">
        <span className="block text-[11px] font-bold opacity-70">{overline}</span>
        <span className="block text-lg font-extrabold">{name}</span>
      </span>
    </a>
  );
}

export function GetAppButton({
  className = 'btn-primary',
  children = 'Get the app',
}: {
  className?: string;
  children?: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const firstStoreRef = useRef<HTMLAnchorElement>(null);
  const first = preferredStore();
  const second: Store = first === 'apple' ? 'google' : 'apple';

  useEffect(() => {
    if (!open) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    firstStoreRef.current?.focus();

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', closeOnEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', closeOnEscape);
      triggerRef.current?.focus();
    };
  }, [open]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={className}
        onClick={() => setOpen(true)}
      >
        {children}
      </button>

      {open &&
        createPortal(
          <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-ink/75 p-5 backdrop-blur-sm"
            onMouseDown={(event) => {
              if (event.currentTarget === event.target) setOpen(false);
            }}
          >
            <section
              role="dialog"
              aria-modal="true"
              aria-labelledby="get-app-title"
              aria-describedby="get-app-description"
              className="relative w-full max-w-md rounded-frame bg-paper p-7 text-ink shadow-2xl md:p-9"
            >
              <button
                type="button"
                aria-label="Close"
                className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-ink/[0.06] text-xl font-bold hover:bg-ink/10"
                onClick={() => setOpen(false)}
              >
                ×
              </button>

              <div className="flex h-12 w-12 items-center justify-center rounded-card bg-mango text-xl font-extrabold">
                D
              </div>
              <p className="overline mt-6">Get the app</p>
              <h2 id="get-app-title" className="mt-2 pr-8 text-3xl leading-tight">
                Download Dala.
              </h2>
              <p
                id="get-app-description"
                className="mt-3 text-[15px] font-medium leading-relaxed text-ink/60"
              >
                Rides, food, stores and parcels across MABAMA. Free to
                download for iPhone and Android.
              </p>

              <div className="mt-7 space-y-3">
                <StoreButton store={first} primary linkRef={firstStoreRef} />
                <StoreButton store={second} primary={false} />
              </div>

              <a
                href={site.facebookUrl}
                target="_blank"
                rel="noreferrer"
                className="mt-5 flex items-center justify-center gap-2 text-sm font-extrabold text-ink/60 hover:text-ink"
              >
                <FacebookIcon />
                Follow Dala on Facebook
              </a>
              <button
                type="button"
                className="mt-3 w-full py-2 text-sm font-extrabold text-ink/50 hover:text-ink"
                onClick={() => setOpen(false)}
              >
                Maybe later
              </button>
            </section>
          </div>,
          document.body,
        )}
    </>
  );
}
